import React, { useEffect, useState } from "react";
import {
    Box, Text, Stack, Button, IconAdd, IconClear, IconDelete, IconEdit,
    Dropdown, TextField, Inline, Checkbox, Card, Columns, Column
} from "braid-design-system";
import { useTranslation } from "react-i18next";
import { OfferCriteriaRow, CRITERIA_TYPE_OPTIONS } from "./types";
import {
    getAllItemGroups,
    ItemGroupResponse,
} from "../../services/itemGroupService";
import {
    getAllPaymentModes,
    PaymentModeResponse,
} from "../../services/paymentModeService";
import {
    getAllPaymentModeDetails,
    PaymentModeDetailResponse,
} from "../../services/paymentModeDetailService";
import {
    getLocations,
    LocationResponse,
} from "../../services/locationService";
import {
    getAllCustomerGroups,
    searchItems,
    CustomerGroupResponse,
} from "../../services/promotionService";

interface PromotionOfferCriteriaSectionProps {
    criteria: OfferCriteriaRow[];
    onChange: (criteria: OfferCriteriaRow[]) => void;
    isReadOnly?: boolean;
    error?: string;
    allowedCriteriaTypes?: string[];
    selectedTypeId?: number | null;
}

const PromotionOfferCriteriaSection: React.FC<PromotionOfferCriteriaSectionProps> = ({
    criteria,
    onChange,
    isReadOnly = false,
    error,
    allowedCriteriaTypes = [],
}) => {
    const { t } = useTranslation();

    const [itemGroups, setItemGroups] = useState<ItemGroupResponse[]>([]);
    const [paymentModes, setPaymentModes] = useState<PaymentModeResponse[]>([]);
    const [paymentModeDetails, setPaymentModeDetails] = useState<PaymentModeDetailResponse[]>([]);
    const [locations, setLocations] = useState<LocationResponse[]>([]);
    const [customerGroups, setCustomerGroups] = useState<CustomerGroupResponse[]>([]);
    const [itemSearchResults, setItemSearchResults] = useState<{ item_code: string; item_name: string }[]>([]);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        getAllItemGroups().then(setItemGroups).catch(() => {});
        getAllPaymentModes().then(setPaymentModes).catch(() => {});
        getAllPaymentModeDetails().then(setPaymentModeDetails).catch(() => {});
        getLocations().then(setLocations).catch(() => {});
        getAllCustomerGroups().then(setCustomerGroups).catch(() => {});
    }, []);

    // Filter the criteria type options based on allowed types
    const filteredCriteriaOptions = React.useMemo(() => {
        if (!allowedCriteriaTypes || allowedCriteriaTypes.length === 0) {
            // If no allowed types specified, show all options (fallback)
            return CRITERIA_TYPE_OPTIONS;
        }
        return CRITERIA_TYPE_OPTIONS.filter(
            (opt) => allowedCriteriaTypes.includes(opt.value)
        );
    }, [allowedCriteriaTypes]);

    const getSelectedValues = (value?: string | null): string[] =>
        value ? value.split(',').map((s) => s.trim()).filter(Boolean) : [];

    const uniq = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

    const handleAddCriteria = () => {
        // Set default type to the first allowed criteria type (excluding location since it's auto-added)
        const defaultType = filteredCriteriaOptions.find(
            (opt) => opt.value !== 'location'
        )?.value || 'item_group';

        const newRow: OfferCriteriaRow = {
            id: undefined,
            line_seq: criteria.length,
            criteria_type: defaultType,
            criteria_value: '',
            amount_min: null,
            amount_max: null,
        };
        onChange([...criteria, newRow]);
        setExpandedIndex(criteria.length);
    };

    const handleRemoveCriteria = (index: number) => {
        const updated = criteria.filter((_, i) => i !== index);
        onChange(updated);
        if (expandedIndex === index) setExpandedIndex(null);
    };

    const handleCriteriaChange = (index: number, field: keyof OfferCriteriaRow, value: any) => {
        const updated = criteria.map((row, i) => {
            if (i === index) {
                const nextRow = { ...row, [field]: value };
                if (field === 'criteria_type') {
                    return {
                        ...nextRow,
                        criteria_value: '',
                        amount_min: null,
                        amount_max: null,
                    };
                }
                return nextRow;
            }
            return row;
        });
        onChange(updated);
    };

    const handleItemSearch = async (value: string) => {
        setSearchValue(value);
        if (value.trim().length < 2) {
            setItemSearchResults([]);
            return;
        }
        const results = await searchItems(value.trim());
        setItemSearchResults(results);
    };

    const handleItemSelect = (index: number, item: { item_code: string; item_name: string }) => {
        const currentVal = criteria[index].criteria_value;
        const codes = currentVal ? currentVal.split(',').map((s) => s.trim()) : [];
        if (!codes.includes(item.item_code)) {
            codes.push(item.item_code);
            handleCriteriaChange(index, 'criteria_value', codes.join(','));
        }
        setSearchValue('');
        setItemSearchResults([]);
    };

    const toggleSelection = (index: number, value: string) => {
        const selected = getSelectedValues(criteria[index].criteria_value);
        const idx = selected.indexOf(value);
        if (idx >= 0) {
            selected.splice(idx, 1);
        } else {
            selected.push(value);
        }
        handleCriteriaChange(index, 'criteria_value', uniq(selected).join(','));
    };

    const removeSelection = (index: number, value: string) => {
        const selected = getSelectedValues(criteria[index].criteria_value).filter((item) => item !== value);
        handleCriteriaChange(index, 'criteria_value', selected.join(','));
    };

    const getSelectedLabel = (row: OfferCriteriaRow): string => {
        if (row.criteria_type === 'amount_range') {
            if (row.amount_min == null && row.amount_max == null) return 'None selected';
            return `${row.amount_min ?? 0} - ${row.amount_max ?? '∞'}`;
        }
        if (!row.criteria_value) return 'None selected';
        const items = getSelectedValues(row.criteria_value);
        if (items.length === 0) return 'None selected';
        return `${items.length} selected`;
    };

    const renderSelectedItemChips = (row: OfferCriteriaRow, index: number) => {
        const items = getSelectedValues(row.criteria_value);
        if (items.length === 0) {
            return <Text size="small" tone="secondary">None selected</Text>;
        }

        return (
            <Inline space="xxsmall">
                {items.map((item) => (
                    <Box
                        key={item}
                        background="neutralLight"
                        paddingX="xsmall"
                        paddingY="xxsmall"
                        borderRadius="standard"
                    >
                        <Inline space="xxsmall" alignY="center">
                            <Text size="xsmall">{item}</Text>
                            {!isReadOnly && (
                                <Box cursor="pointer" onClick={() => removeSelection(index, item)}>
                                    <IconClear size="small" tone="secondary" />
                                </Box>
                            )}
                        </Inline>
                    </Box>
                ))}
            </Inline>
        );
    };

    const renderExpandedContent = (row: OfferCriteriaRow, index: number) => {
        switch (row.criteria_type) {
            case 'item_group':
                return (
                    <Stack space="xsmall">
                        <Text size="small" weight="strong">Select Item Groups</Text>
                        <Columns space="small" collapseBelow="tablet">
                            <Column width="1/2">
                                <Stack space="xsmall">
                                    {itemGroups.map((g) => {
                                        const selected = row.criteria_value?.split(',').map(s => s.trim()).filter(Boolean).includes(g.group_code || '');
                                        return (
                                            <Checkbox
                                                key={g.group_code}
                                                id={`criteria-${index}-opt-${g.group_code}`}
                                                label={`${g.group_code || ''} - ${g.group_name || ''}`}
                                                checked={selected}
                                                onChange={(_e) => toggleSelection(index, g.group_code || '')}
                                                disabled={isReadOnly}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Column>
                        </Columns>
                    </Stack>
                );

            case 'item':
                return (
                    <Stack space="xsmall">
                        <TextField
                            id={`item-search-${index}`}
                            label="Search Items"
                            placeholder="Type item code..."
                            value={searchValue}
                            onChange={(e) => handleItemSearch(e.currentTarget.value)}
                            disabled={isReadOnly}
                        />
                        {itemSearchResults.length > 0 && (
                            <Card>
                                <Stack space="xxsmall">
                                    {itemSearchResults.map((item) => (
                                        <Box
                                            key={item.item_code}
                                            cursor="pointer"
                                            onClick={() => handleItemSelect(index, item)}
                                            paddingY="xxsmall"
                                        >
                                            <Text size="small">{item.item_code} - {item.item_name}</Text>
                                        </Box>
                                    ))}
                                </Stack>
                            </Card>
                        )}
                        {renderSelectedItemChips(row, index)}
                    </Stack>
                );

            case 'amount_range':
                return (
                    <Columns space="small" alignY="center">
                        <Column width="1/2">
                            <TextField
                                id={`amount-min-${index}`}
                                label="From"
                                placeholder="0.00"
                                value={row.amount_min != null ? String(row.amount_min) : ''}
                                onChange={(e) => handleCriteriaChange(index, 'amount_min', e.currentTarget.value ? Number(e.currentTarget.value) : null)}
                                disabled={isReadOnly}
                            />
                        </Column>
                        <Column width="1/2">
                            <TextField
                                id={`amount-max-${index}`}
                                label="To"
                                placeholder="999999.99"
                                value={row.amount_max != null ? String(row.amount_max) : ''}
                                onChange={(e) => handleCriteriaChange(index, 'amount_max', e.currentTarget.value ? Number(e.currentTarget.value) : null)}
                                disabled={isReadOnly}
                            />
                        </Column>
                    </Columns>
                );

            case 'payment_mode':
                const selectedPaymentModeCodes = getSelectedValues(row.criteria_value);
                const cardDetailsForParent = paymentModeDetails;
                const isCardPaymentMode = (pm: PaymentModeResponse): boolean =>
                    pm.paycode === 'CRD' || (pm.paytypedesc || '').trim().toLowerCase() === 'card';
                return (
                    <Stack space="xsmall">
                        <Text size="small" weight="strong">Select Payment Modes</Text>
                        <Columns space="small" collapseBelow="tablet">
                            <Column width="1/2">
                                <Stack space="xsmall">
                                    {paymentModes.map((pm) => {
                                        const selected = selectedPaymentModeCodes.includes(pm.paycode);
                                        const isCardMode = isCardPaymentMode(pm);
                                        return (
                                            <Stack key={pm.paycode} space="xxsmall">
                                                <Checkbox
                                                    id={`criteria-${index}-opt-${pm.paycode}`}
                                                    label={isCardMode ? `${pm.paycode} - Any card type` : `${pm.paycode} - ${pm.paytypedesc || ''}`}
                                                    checked={selected}
                                                    onChange={(_e) => {
                                                        const nextSelected = selected
                                                            ? selectedPaymentModeCodes.filter(code => code !== pm.paycode)
                                                            : [...selectedPaymentModeCodes, pm.paycode];
                                                        handleCriteriaChange(index, 'criteria_value', uniq(nextSelected).join(','));
                                                    }}
                                                    disabled={isReadOnly}
                                                />
                                                {isCardMode && cardDetailsForParent.length > 0 && (
                                                    <Box marginLeft="large" marginTop="xxsmall">
                                                        <Stack space="xsmall">
                                                            <Text size="xsmall" tone="secondary">
                                                                Or restrict to specific card types:
                                                            </Text>
                                                            {cardDetailsForParent.map((cd) => {
                                                                const cardSelected = selectedPaymentModeCodes.includes(cd.paycode);
                                                                return (
                                                                    <Checkbox
                                                                        key={cd.paycode}
                                                                        id={`criteria-${index}-card-${cd.paycode}`}
                                                                        label={cd.paycode}
                                                                        checked={cardSelected}
                                                                        onChange={(_e) => {
                                                                            const next = cardSelected
                                                                                ? selectedPaymentModeCodes.filter(code => code !== cd.paycode)
                                                                                : [...selectedPaymentModeCodes, cd.paycode];
                                                                            handleCriteriaChange(index, 'criteria_value', uniq(next).join(','));
                                                                        }}
                                                                        disabled={isReadOnly}
                                                                    />
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Column>
                        </Columns>
                    </Stack>
                );

            case 'location':
                return (
                    <Stack space="xsmall">
                        <Text size="small" weight="strong">Select Locations</Text>
                        <Columns space="small" collapseBelow="tablet">
                            <Column width="1/2">
                                <Stack space="xsmall">
                                    {locations.map((loc) => {
                                        const selected = row.criteria_value?.split(',').map(s => s.trim()).filter(Boolean).includes(loc.loc_code || '');
                                        return (
                                            <Checkbox
                                                key={loc.loc_code}
                                                id={`criteria-${index}-opt-${loc.loc_code}`}
                                                label={`${loc.loc_code || ''} - ${loc.loc_desc || ''}`}
                                                checked={selected}
                                                onChange={(_e) => toggleSelection(index, loc.loc_code || '')}
                                                disabled={isReadOnly}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Column>
                        </Columns>
                    </Stack>
                );

            case 'customer_group':
                return (
                    <Stack space="xsmall">
                        <Text size="small" weight="strong">Select Customer Groups</Text>
                        <Columns space="small" collapseBelow="tablet">
                            <Column width="1/2">
                                <Stack space="xsmall">
                                    {customerGroups.map((cg) => {
                                        const groupId = cg.id != null ? String(cg.id) : '';
                                        const selected = getSelectedValues(row.criteria_value).includes(groupId);
                                        return (
                                            <Checkbox
                                                key={groupId}
                                                id={`criteria-${index}-opt-${groupId}`}
                                                label={cg.group_name || ''}
                                                checked={selected}
                                                onChange={(_e) => toggleSelection(index, groupId)}
                                                disabled={isReadOnly || !groupId}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Column>
                        </Columns>
                    </Stack>
                );

            default:
                return null;
        }
    };

    const isExpanded = (index: number) => expandedIndex === index;

    return (
        <Card>
            <Stack space="medium">
                <Inline space="small" alignY="center">
                    <Text weight="strong" size="large">
                        {t('page.promotion.offer_criteria.title', 'Offer Criteria')}
                    </Text>
                    {!isReadOnly && (
                        <Button
                            variant="ghost"
                            tone="brandAccent"
                            icon={<IconAdd />}
                            onClick={handleAddCriteria}
                            size="small"
                        >
                            {t('page.promotion.offer_criteria.add', 'Add Criteria')}
                        </Button>
                    )}
                </Inline>
                {error ? (
                    <Text size="small" tone="critical">
                        {error}
                    </Text>
                ) : null}

                {criteria.length === 0 ? (
                    <Box paddingY="medium">
                        <Text tone="secondary">
                            {t('page.promotion.offer_criteria.empty', 'No criteria defined. Add criteria to specify when this promotion applies.')}
                        </Text>
                    </Box>
                ) : (
                    <Stack space="small">
                        {criteria.map((row, index) => (
                            <Card key={`criteria-${index}`} tone="formAccent">
                                <Stack space="small">
                                    <Columns space="small" alignY="center">
                                        <Column width="2/5">
                                            <Dropdown
                                                id={`criteria-type-${index}`}
                                                aria-label="Criteria Type"
                                                value={row.criteria_type}
                                                onChange={(e) => {
                                                    handleCriteriaChange(index, 'criteria_type', e.target.value);
                                                    setExpandedIndex(index);
                                                }}
                                                disabled={isReadOnly}
                                            >
                                                {filteredCriteriaOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Dropdown>
                                        </Column>
                                        <Column width="3/5">
                                            <Box display="flex" justifyContent="flexEnd" alignItems="center">
                                                <Inline space="small" alignY="center">
                                                    <Text size="small" tone="secondary">
                                                        {getSelectedLabel(row)}
                                                    </Text>
                                                    {!isReadOnly && (
                                                        <>
                                                            <Box cursor="pointer" onClick={() => setExpandedIndex(isExpanded(index) ? null : index)}>
                                                                <IconEdit tone="info" size="small" />
                                                            </Box>
                                                            <Box cursor="pointer" onClick={() => handleRemoveCriteria(index)}>
                                                                <IconDelete tone="critical" size="small" />
                                                            </Box>
                                                        </>
                                                    )}
                                                </Inline>
                                            </Box>
                                        </Column>
                                    </Columns>

                                    {isExpanded(index) && (
                                        <Box marginTop="xsmall">
                                            {renderExpandedContent(row, index)}
                                        </Box>
                                    )}
                                </Stack>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Card>
    );
};

export default PromotionOfferCriteriaSection;